// The app-level answer to a dead session. Both token getters and the
// transport's 401 handler dispatch SESSION_EXPIRED_EVENT; this component
// is the single listener that turns it into an interactive re-auth,
// preserving the route the user was on. The shell's session cookie
// usually outlives the SPA's refresh token, so the round-trip is
// silent — the user lands back where they were with a fresh grant.

import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { useAuth } from 'react-oidc-context';

import { SESSION_EXPIRED_EVENT } from '@/lib/auth/session';

// One automatic redirect per window: if the session is still dead after a
// fresh round-trip (server misconfig, clock skew), looping through the shell
// would trap the user. Surviving requests keep their error states instead.
const REDIRECT_THROTTLE_MS = 60_000;
const THROTTLE_KEY = 'knowsh:reauth-at';

function recentlyRedirected(): boolean {
  const raw = window.sessionStorage.getItem(THROTTLE_KEY);
  const at = raw ? Number.parseInt(raw, 10) : Number.NaN;
  return Number.isFinite(at) && Date.now() - at < REDIRECT_THROTTLE_MS;
}

export function SessionRecovery() {
  const { isLoading, signinRedirect } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (isLoading) return;
    const onExpired = () => {
      if (recentlyRedirected()) return;
      window.sessionStorage.setItem(THROTTLE_KEY, String(Date.now()));
      void signinRedirect({
        state: { returnTo: location.pathname + location.search + location.hash },
      });
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [isLoading, signinRedirect, location.pathname, location.search, location.hash]);

  return null;
}
